import { Flex } from "@/components/atoms/flex";
import { useStyle } from "./style";
import { PanelHeader } from "@/components/atoms/panelHeader";
import {
	Accordion,
	AccordionHeader,
	AccordionItem,
	AccordionPanel,
	Text,
	mergeClasses,
} from "@fluentui/react-components";
import { useIntl } from "react-intl";
import { ClearableInput } from "@/components/atoms/clearableInput";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppCacheState, CacheKey, useAppCacheStore } from "@/common/stores/persistingStores/app";
import { Search24Regular } from "@fluentui/react-icons";
import { QueueWithResources } from "@/repository/models";
import { List, ListItemProps } from "@/components/atoms/list";
import { openPopup } from "@/common/helpers/popupHelper";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { useShadowStyle } from "@/common/theme/shadowStyles";
import { ResourcesPanelProps } from "./types";
import { shadowByPosition } from "@/common/theme/styleUtils";
import { useAuth } from "@/hooks/authentication";
import { useQueuesWithResourcesStore } from "@/common/stores/nonpersistingStores/queues";

export const ResourcesPanel = (props: ResourcesPanelProps) => {
	const { shadowTo } = props;
	const classes = useStyle();
	const authContext = useAuth();
	const intl = useIntl();
	const shadowStyle = useShadowStyle();

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const [searchText, setSearchText] = useState<string>(
		appStoreCache[CacheKey.ResourcesSearch] ?? ""
	);
	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const queues = useQueuesWithResourcesStore((state) => state.queues);

	const [filteredQueues, setFilteredQueues] = useState<QueueWithResources[]>(
		[]
	);

	useEffect(() => {
		if (!queues) {
			return;
		}

		const queuesWithResources = queues.filter((q) => !!q.resources?.length);

		if (!searchText) {
			setFilteredQueues(queuesWithResources);

			return;
		}

		const filteredQueuesWithResources = queuesWithResources
			.map((q) => ({
				...q,
				resources: q.resources.filter((r) =>
					r.name.toLowerCase().includes(searchText.toLowerCase())
				),
			}))
			.filter((q) => q.resources?.length);

		setFilteredQueues(filteredQueuesWithResources);
	}, [searchText, queues]);

	const buildAccordionItems = useCallback(
		() =>
			filteredQueues.map((q, index) => {
				const queueResourcesItems = q.resources
					?.filter(
						(qr) =>
							currentInteraction?.queueId === q.id || !qr.inMediaHandlingOnly
					)
					.map(
						(qr) =>
							({
								key: qr.id,
								header: qr.name,
								content: new URL(qr.url).origin,
								onClick: () => openPopup(qr.url),
							}) as ListItemProps
					);

				if (!queueResourcesItems.length) {
					return <></>;
				}

				return (
					<AccordionItem key={q.id} value={index}>
						<AccordionHeader className={classes.accordionHeader}>
							<Text>{q.name}</Text>
							<Text weight="bold">({queueResourcesItems?.length ?? 0})</Text>
						</AccordionHeader>
						<AccordionPanel className={classes.accordionPanel}>
							<List
								classesName={{
									mainText: classes.listItemHeader,
									item: classes.accordionItem,
									shadow: shadow,
								}}
								items={queueResourcesItems}
							/>
						</AccordionPanel>
					</AccordionItem>
				);
			}),
		[filteredQueues, currentInteraction, authContext]
	);

	const activeInteractionQueueIndex = useMemo<number>(
		() => filteredQueues.findIndex((q) => q.id === currentInteraction?.queueId),
		[filteredQueues, currentInteraction]
	);

	const shadow = useMemo<string | undefined>(
		() => shadowByPosition(shadowTo, shadowStyle),
		[shadowTo, shadowStyle]
	);

	const hasSearchResults = !!filteredQueues.length;

	return (
		<Flex column className={mergeClasses(classes.container, shadow)}>
			<PanelHeader
				left={
					<Text className={classes.headerTitle}>
						{intl.formatMessage({ id: "Resources.HeaderTitle" })}
					</Text>
				}
			/>
			<ClearableInput
				clearable
				value={searchText}
				className={classes.searchInput}
				placeholder={intl.formatMessage({ id: "Resources.SearchPlaceholder" })}
				contentAfter={<Search24Regular />}
				onChange={setSearchText}
			/>
			{!hasSearchResults && (
				<Text className={classes.noResultText}>
					{intl.formatMessage({ id: "Resources.SearchNoResults" })}
				</Text>
			)}
			{hasSearchResults && activeInteractionQueueIndex >= 0 && (
				<Accordion
					multiple
					collapsible
					defaultOpenItems={[activeInteractionQueueIndex]}
					className={classes.accordion}
				>
					{buildAccordionItems()}
				</Accordion>
			)}
			{hasSearchResults && activeInteractionQueueIndex < 0 && (
				<Accordion multiple collapsible className={classes.accordion}>
					{buildAccordionItems()}
				</Accordion>
			)}
		</Flex>
	);
};
